import { normalizeRole } from '../../rbac';

/** Roles permitted to view data exchanged with the DHDA B2G Gateway. */
export const B2G_VIEW_ROLES = [
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'FACILITY_MANAGER',
  'REGION_MANAGER',
  'CLINICAL_LEAD',
] as const;

/** Roles permitted to prepare or submit provider data to the DHDA B2G Gateway. */
export const B2G_SUBMIT_ROLES = [
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'FACILITY_MANAGER',
] as const;

/**
 * Roles on local QI capture operations. Tenant Admin is included (Paul,
 * 26 Oct 2026); System Admin stays excluded from tenant QI data.
 */
export const QI_LOCAL_ENTRY_ROLES = [
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'COORDINATOR',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'TENANT_ADMIN',
] as const;

/** Regulator/local reconciliation cohort for local QI source data. */
export const QI_LOCAL_SOURCE_ROLES = [
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'COORDINATOR',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'FINANCE_OFFICER',
  'TENANT_ADMIN',
] as const;

/**
 * Roles that may view manual (guided GPMS) QI submissions and lodgements.
 * Tenant Admin is added to the B2G view cohort for the manual route only;
 * B2G gateway data keeps B2G_VIEW_ROLES (DHDA FNDN-160 separates privileged
 * administration accounts from gateway data roles).
 */
export const QI_MANUAL_SUBMISSION_VIEW_ROLES = [...B2G_VIEW_ROLES, 'TENANT_ADMIN'] as const;

/** Roles that may record a manual QI submission or GPMS lodgement (AAL2 still required). */
export const QI_MANUAL_SUBMISSION_ROLES = [...B2G_SUBMIT_ROLES, 'TENANT_ADMIN'] as const;

/** Roles allowed to open the mixed QI data and submission route. */
export const QI_LOCAL_ROUTE_ROLES = [
  ...B2G_VIEW_ROLES,
  ...QI_LOCAL_ENTRY_ROLES,
  ...QI_LOCAL_SOURCE_ROLES,
] as const;

/**
 * Roles that may read the QI ledger (subject-level clinical rows, resident names and evidence).
 * The local QI route cohort without FINANCE_OFFICER: finance keeps legacy reconciliation
 * reads only and does not need per-resident clinical QI data (minimum necessary).
 */
export const QI_LEDGER_READ_ROLES = QI_LOCAL_ROUTE_ROLES.filter(
  (role) => role !== 'FINANCE_OFFICER',
);

export const B2G_VIEW_DENIED_MESSAGE = 'Your role does not allow viewing this B2G reporting data.';
export const B2G_SUBMIT_DENIED_MESSAGE =
  'Your role does not allow preparing or recording B2G reporting data.';

/** Roles permitted to configure the B2G connection and provider mappings. */
export const B2G_CONFIGURATION_ROLES = ['SYSTEM_ADMIN', 'TENANT_ADMIN'] as const;

/** Roles allowed to open the DHDA settings route, whose data/config panels are separate. */
export const B2G_INTEGRATION_ROUTE_ROLES = [...B2G_VIEW_ROLES, ...B2G_CONFIGURATION_ROLES] as const;

const B2G_VIEW_ROLE_SET: ReadonlySet<string> = new Set(B2G_VIEW_ROLES);
const B2G_SUBMIT_ROLE_SET: ReadonlySet<string> = new Set(B2G_SUBMIT_ROLES);
const B2G_CONFIGURATION_ROLE_SET: ReadonlySet<string> = new Set(B2G_CONFIGURATION_ROLES);
const QI_LOCAL_ENTRY_ROLE_SET: ReadonlySet<string> = new Set(QI_LOCAL_ENTRY_ROLES);
const QI_MANUAL_SUBMISSION_VIEW_ROLE_SET: ReadonlySet<string> = new Set(
  QI_MANUAL_SUBMISSION_VIEW_ROLES,
);
const QI_MANUAL_SUBMISSION_ROLE_SET: ReadonlySet<string> = new Set(QI_MANUAL_SUBMISSION_ROLES);

export function canViewB2gData(role: string | null | undefined): boolean {
  const normalizedRole = normalizeRole(role);
  return normalizedRole !== null && B2G_VIEW_ROLE_SET.has(normalizedRole);
}

export function canSubmitB2gData(role: string | null | undefined): boolean {
  const normalizedRole = normalizeRole(role);
  return normalizedRole !== null && B2G_SUBMIT_ROLE_SET.has(normalizedRole);
}

export function canViewQiManualSubmission(role: string | null | undefined): boolean {
  const normalizedRole = normalizeRole(role);
  return normalizedRole !== null && QI_MANUAL_SUBMISSION_VIEW_ROLE_SET.has(normalizedRole);
}

export function canRecordQiManualSubmission(role: string | null | undefined): boolean {
  const normalizedRole = normalizeRole(role);
  return normalizedRole !== null && QI_MANUAL_SUBMISSION_ROLE_SET.has(normalizedRole);
}

export function canEnterLocalQi(role: string | null | undefined): boolean {
  const normalizedRole = normalizeRole(role);
  return normalizedRole !== null && QI_LOCAL_ENTRY_ROLE_SET.has(normalizedRole);
}

export function canConfigureB2gIntegration(role: string | null | undefined): boolean {
  const normalizedRole = normalizeRole(role);
  return normalizedRole !== null && B2G_CONFIGURATION_ROLE_SET.has(normalizedRole);
}

/** The DHDA settings route contains separate data and configuration panels. */
export function canAccessB2gIntegration(role: string | null | undefined): boolean {
  return canViewB2gData(role) || canConfigureB2gIntegration(role);
}

/** Preserve authentication/module failures; translate only the role refusal. */
export function b2gGuardError(error: string | undefined, capability: 'view' | 'submit'): string {
  if (
    error &&
    error !== 'Not authorised' &&
    error !== 'Not authorized' &&
    error !== 'Insufficient permissions'
  )
    return error;
  return capability === 'view' ? B2G_VIEW_DENIED_MESSAGE : B2G_SUBMIT_DENIED_MESSAGE;
}

export function canReadLocalQiSource(role: string | null | undefined): boolean {
  const normalizedRole = normalizeRole(role);
  return (
    normalizedRole !== null && (QI_LOCAL_SOURCE_ROLES as readonly string[]).includes(normalizedRole)
  );
}
